import clsx from 'clsx'
import dayjs, { Dayjs } from 'dayjs'
import { nothing, PropertyValueMap, unsafeCSS } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { html } from 'lit/static-html.js'

import {
  CommonFormElementSize,
  FloatingStrategy,
  PopupPlacement,
} from '@/common/types'
import { getValidityStateObject } from '@/common/utils'
import { GenericFormElement } from '@/internal/mixin/genericFormElement'
import { translate } from '@/localization/localization'

import DapDSCalendar from '../calendar/calendar.component'
import styles from './datepicker.scss?inline'

/**
 * `dap-ds-datepicker`
 * @summary A datepicker is a graphical user interface widget that allows the user to select a date from a calendar and/or time from a time range.
 * @element dap-ds-datepicker
 * @title - Datepicker
 *
 * @property {Dayjs} value - The value of the datepicker.
 * @property {string} label - The label of the datepicker.
 * @property {string} description - The description of the datepicker.
 * @property {'xs' | 'sm' | 'sm'} size - The size of the datepicker.
 * @property {boolean} disabled - The disabled state of the datepicker.
 * @property {boolean} required - The required state of the datepicker.
 * @property {boolean} readonly - The readonly state of the datepicker.
 * @property {boolean} autofocus - The autofocus state of the datepicker.
 * @property {string} tooltip - The tooltip of the datepicker.
 * @property {'top' | 'right' | 'bottom' | 'left'} tooltipPlacement - The tooltip placement of the datepicker.
 * @property {string} feedback - The feedback of the datepicker.
 * @property {'negative' | 'positive' | 'warning'} feedbackType - The feedback type of the datepicker.
 * @property {boolean} optional - The optional state of the datepicker.
 * @property {string} optionalLabel - The optional label of the datepicker.
 * @property {boolean} subtle - The weight of the label. Default is `false`
 * @property {string} autocomplete - The autocomplete of the datepicker.
 * @property {boolean} autofocus - The autofocus of the datepicker.
 *
 * @event {{ value: string }} dds-change - Fired when the datepicker value changes.
 * @event {{ value: string }} dds-input - Fired when the datepicker input value changes.
 * @event {{ value: string }} dds-valid-date - Fired when the datepicker input value is valid. Happens on manual input typing.
 * @event {{ value: string, type: 'invalid' | 'out-of-range' }} dds-invalid-date - Fired when the datepicker input value is invalid. Happens on manual input typing.
 * @event {{ void }} dds-clear - Fired when the datepicker is cleared.
 * @event {{ void }} dds-focus - Emitted when the datepicker gains focus.
 * @event {{ void }} dds-blur - Emitted when the datepicker loses focus.
 *
 * @csspart base - The main datepicker container.
 * @csspart calendar - The calendar of the datepicker.
 * @csspart trigger - The trigger button of the datepicker.
 * @csspart label - The label of the datepicker.
 * @csspart description - The description of the datepicker.
 * @csspart feedback - The feedback of the datepicker.
 * @csspart tooltip - The tooltip of the datepicker.
 * @csspart input - The input of the datepicker.
 * @csspart clear-button - The clear button of the datepicker.
 *
 * @cssproperty --dds-combobox-background - The background color of the combobox. Defaults to var(--dds-fields-background-default).
 * @cssproperty --dds-combobox-border-color - The border color of the combobox. Defaults to var(--dds-border-neutral-base).
 * @cssproperty --dds-combobox-border-width - The border width of the combobox. Defaults to var(--dds-border-width-base, 1px).
 * @cssproperty --dds-combobox-border-radius - The border radius of the combobox. Defaults to var(--dds-radius-base).
 * @cssproperty --dds-combobox-text-color - The text color of the combobox. Defaults to var(--dds-text-neutral-base).
 * @cssproperty --dds-combobox-placeholder-color - The placeholder text color. Defaults to var(--dds-text-neutral-subtle).
 * @cssproperty --dds-combobox-disabled-background - The background color when disabled. Defaults to var(--dds-background-neutral-stronger).
 * @cssproperty --dds-combobox-disabled-text - The text color when disabled. Defaults to var(--dds-text-neutral-disabled).
 * @cssproperty --dds-combobox-error-border - The border color for error state. Defaults to var(--dds-border-negative-base).
 * @cssproperty --dds-combobox-success-border - The border color for success state. Defaults to var(--dds-border-positive-base).
 * @cssproperty --dds-combobox-icon-color - The color of the icons. Defaults to var(--dds-text-icon-neutral-subtle).
 * @cssproperty --dds-combobox-clear-icon-color - The color of the clear icon. Defaults to var(--dds-button-subtle-icon-neutral-enabled).
 * @cssproperty --dds-combobox-popup-background - The background color of the popup. Defaults to var(--dds-background-neutral-subtle).
 * @cssproperty --dds-combobox-popup-shadow - The box shadow of the popup. Defaults to 0 4px 6px -1px rgb(0 0 0 / 8%), 0 2px 4px -1px rgb(0 0 0 / 6%).
 *
 * @cssproperty --dds-combobox-padding-xs - The padding for extra small size. Defaults to var(--dds-spacing-200).
 * @cssproperty --dds-combobox-padding-sm - The padding for small size. Defaults to var(--dds-spacing-200).
 * @cssproperty --dds-combobox-padding-lg - The padding for large size. Defaults to var(--dds-spacing-300).
 * @cssproperty --dds-combobox-padding-horizontal - The horizontal padding. Defaults to var(--dds-spacing-300).
 * @cssproperty --dds-combobox-padding-vertical - The vertical padding. Defaults to var(--dds-spacing-200).
 * @cssproperty --dds-combobox-gap - The gap between elements. Defaults to var(--dds-spacing-100).
 * @cssproperty --dds-combobox-icon-gap - The gap between icons. Defaults to var(--dds-spacing-200).
 * @cssproperty --dds-combobox-action-gap - The gap between action elements. Defaults to var(--dds-spacing-200).
 * @cssproperty --dds-combobox-action-padding - The padding for action elements. Defaults to var(--dds-spacing-300).
 * @cssproperty --dds-combobox-clear-icon-width - The width of the clear icon. Defaults to var(--dds-spacing-800).
 * @cssproperty --dds-combobox-dropdown-icon-right - The right position of the dropdown icon. Defaults to var(--dds-spacing-600).
 * @cssproperty --dds-combobox-min-width - The minimum width of the combobox. Defaults to 7.5rem.
 *
 */
@customElement('dap-ds-datepicker')
export default class DapDSDatePicker extends GenericFormElement {
  @query('#input') private readonly triggerInput!: HTMLInputElement
  @query('#calendar') private readonly calendar!: DapDSCalendar

  /** The current visible date of the calendar. Only the month and year are considered.
   * @type {Dayjs}
   * @default dayjs()
   */
  @property({ attribute: false }) currentDate = dayjs()

  /** The minimum date of the datepicker. This date will be the minimum selectable date.
   * @type {Dayjs}
   * @default dayjs('1901-01-01')
   */
  @property({ attribute: false }) minDate = dayjs('1901-01-01')

  /** The maximum date of the datepicker. This date will be the maximum selectable date.
   * @type {Dayjs}
   * @default dayjs('2099-12-31')
   */
  @property({ attribute: false }) maxDate = dayjs('2099-12-31')

  /** The disabled date of the datepicker. This function will be called for each date to determine if it is disabled.
   * @type {(date: Dayjs) => boolean}
   * @default (date: Dayjs) => false
   */
  @property() disabledDate = (date: Dayjs) => {
    return !date
  }

  /** The placement of the dropdown of the datepicker.
   * @type {'top-start' | 'top' | 'top-end' | 'right-start' | 'right' | 'right-end' | 'bottom-start' | 'bottom' | 'bottom-end' | 'left-start' | 'left' | 'left-end'}
   */
  @property({ reflect: true }) placement: PopupPlacement = 'bottom-start'

  /** The open state of the datepicker. */
  @property({ type: Boolean, reflect: true }) opened = false

  /** Wheter the calendar should open on typing. */
  @property({ type: Boolean }) openCalendarOnInput = false

  /** Wheter the calendar should send an empty event on typing. */
  @property({ type: Boolean }) sendEmptyEventOnInput = false

  /** The placeholder of the datepicker. */
  @property() placeholder: string | null

  /** The loading state of the datepicker. */
  @property({ type: Boolean }) loading?: boolean = false

  /** The format of the datepicker. This is a comma separated list of [formats](https://day.js.org/docs/en/display/format). */
  @property({ type: String }) format?: string

  /** The format separator character in the format attribute. */
  @property({ type: String }) formatSeparator: string = '|'

  /** Whether the clear button should be shown. */
  @property({ type: String }) clearButton?: string = 'true'

  /** The aria label of the clear button. */
  @property({ type: String }) clearButtonAriaLabel?: string

  /** The floating strategy of the datepicker. */
  @property({ type: String }) floatingStrategy: FloatingStrategy = 'fixed'

  /** The locale of the datepicker. By default it uses the global locale from dayjs. It is determined from the browser language.
   * @type {'hu' | 'en'}
   */
  @property({ type: String }) locale: string = dayjs.locale()

  @state() private formatOptions: string[] = []
  @state() private setValueState: boolean = true

  static readonly styles = unsafeCSS(styles)

  protected firstUpdated(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    super.firstUpdated(_changedProperties)

    if (this.value) {
      this.calendar.value = dayjs(this.value).locale(this.locale)
    }
    this.calendar.currentDate = this.currentDate
    this.calendar.minDate = this.minDate
    this.calendar.maxDate = this.maxDate
    this.calendar.disabledDate = this.disabledDate

    this.formatOptions = this.format?.split(this.formatSeparator) || []
  }

  protected updated(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    super.updated(_changedProperties)

    if (_changedProperties.has('value')) {
      this.calendar.value = this.value
      if (this.setValueState) {
        this.triggerInput.value = this.getFormattedValue()
      }
      this.setValidity()
    }

    if (_changedProperties.has('currentDate')) {
      this.calendar.currentDate = this.currentDate
    }
  }

  getFormattedValue() {
    if (!this.value) {
      return ''
    }

    return dayjs(this.value)
      .locale(this.locale)
      .format(this.formatOptions[0] || 'L')
  }

  isValidFormat(value: string) {
    if (!this.formatOptions.length) {
      return dayjs(value, { format: 'L', locale: this.locale }).isValid()
    }

    return this.formatOptions.some(format =>
      dayjs(value, { format, locale: this.locale }).isValid(),
    )
  }

  isValueValid() {
    if (!this.value) {
      return true
    }

    return this.isInputValid()
  }

  isInputValid() {
    if (this.formatOptions.length) {
      return this.isValidFormat(this.triggerInput.value)
    }

    return dayjs(this.triggerInput.value, {
      format: 'L',
      locale: this.locale,
    }).isValid()
  }

  setValue(
    value?: Dayjs,
    setInputValue: boolean = true,
    sendEmptyEventOnInput: boolean = true,
  ) {
    this.value = value

    if (setInputValue) {
      this.setValueState = true
      this.triggerInput.value = this.getFormattedValue()
    } else {
      this.setValueState = false
    }

    this.setValidity()
    this.hideCalendar()

    if (sendEmptyEventOnInput) {
      this.emit('dds-change', { value: this.value })
      this.emit('dds-valid-date', { value: this.triggerInput.value })
    }
  }

  async clearSelection() {
    this.setValue(undefined, true, true)
  }

  private readonly handleDocumentMouseDown = (event: MouseEvent) => {
    const path = event.composedPath()
    if (this && !path.includes(this)) {
      this.hideCalendar()
    }
  }

  private readonly handleInput = (event: InputEvent) => {
    const input = event.target as HTMLInputElement

    if (!input.value) {
      this.setValue(undefined, false, true)
    } else {
      if (this.isInputValid()) {
        const date = dayjs(input.value).locale(this.locale)

        if (
          date.isBefore(this.minDate, 'day') ||
          date.isAfter(this.maxDate, 'day')
        ) {
          this.setValue(undefined, false, this.sendEmptyEventOnInput)
          this.emit('dds-invalid-date', {
            value: input.value,
            type: 'out-of-range',
          })
        } else {
          this.setValue(date, false)
          this.currentDate = date
          this.emit('dds-valid-date', { value: input.value })
        }
      } else {
        this.setValue(undefined, false, this.sendEmptyEventOnInput)
        this.emit('dds-invalid-date', { value: input.value, type: 'invalid' })
      }
    }

    if (this.openCalendarOnInput && !this.opened) {
      this.showCalendar()
    }

    this.emit('dds-input', { value: input.value })
  }

  private readonly handleKeyDown = async (event: KeyboardEvent) => {
    if (event.key === 'Tab') {
      return
    }

    if (
      event.key === 'Enter' ||
      event.key === 'ArrowDown' ||
      event.key === 'ArrowUp'
    ) {
      event.preventDefault()
      event.stopPropagation()

      if (!this.opened) {
        this.showCalendar()
        await this.updateComplete
        this.calendar.captureFocus()
      } else {
        this.hideCalendar()
      }
    }
  }

  public get focusElement() {
    return this.triggerInput
  }

  private readonly handleClick = () => {
    if (!this.opened) {
      this.showCalendar()
    } else {
      this.hideCalendar()
    }
  }

  private readonly handleClearClick = () => {
    if (!this.disabled) {
      this.clearSelection()

      this.triggerInput.focus()
      this.setValidity()

      this.hideCalendar()

      this.emit('dds-clear')
    }
  }

  async showCalendar() {
    if (this.disabled || this.readonly || this.opened) {
      return
    }

    this.opened = true

    document.addEventListener('mousedown', this.handleDocumentMouseDown)
  }

  async hideCalendar() {
    if (!this.opened) {
      return
    }

    this.opened = false

    document.removeEventListener('mousedown', this.handleDocumentMouseDown)

    await this.updateComplete
    this.focus()
  }

  private readonly handleFocus = () => {
    this.emit('dds-focus')
  }

  private readonly handleBlur = () => {
    this.emit('dds-blur')
  }

  // custom validation
  getValidity() {
    return !!this.value
  }

  get validity(): ValidityState {
    if (this.internals.validity) {
      return this.internals.validity
    } else {
      return getValidityStateObject({
        valueMissing: this.required && !this.getValidity(),
        patternMismatch: !this.isValueValid(),
      })
    }
  }

  checkValidity() {
    return this.required && this.getValidity() && this.isValueValid()
  }

  reportValidity() {
    return this.required && this.getValidity() && this.isValueValid()
  }

  setValidity() {
    if (!this.checkValidity()) {
      this.internals.setValidity(
        {
          valueMissing: this.required && !this.getValidity(),
          patternMismatch: !this.isValueValid(),
        },
        this.validationMessage,
        this.triggerInput,
      )
    } else {
      this.internals.setValidity({})
    }
  }

  handleInvalid(event: Event) {
    event.preventDefault()
  }

  render() {
    return html` <div class="label-container">
        ${this.label &&
        html`<dap-ds-form-label
          part="label"
          id="label"
          .size=${this.size}
          .required=${this.required}
          .optional=${this.optional}
          .disabled=${this.disabled}
          class=${clsx('label', {
            'label--has-description': !this.description,
          })}
          for="input"
          label=${ifDefined(this.label)}>
        </dap-ds-form-label>`}
        ${this.tooltip &&
        html` <dap-ds-tooltip
          part="tooltip"
          content=${this.tooltip}
          mode="toggle"
          placement=${this.tooltipPlacement}>
          <dap-ds-icon-button slot="trigger" size="lg" clean>
            <dap-ds-icon
              name="information-line"
              aria-label=${this.tooltipAriaLabel}
              class="info-button"></dap-ds-icon>
          </dap-ds-icon-button>
        </dap-ds-tooltip>`}
      </div>
      ${this.description &&
      html`
        <dap-ds-typography
          part="description"
          variant="description"
          size=${ifDefined(this.size as CommonFormElementSize)}
          id="description">
          ${this.description}
        </dap-ds-typography>
      `}
      <div>
        <dap-ds-popup
          part="base"
          class="select-popup"
          overflow
          ?opened=${this.opened}
          ?hasArrow=${false}
          placement=${this.placement}
          floatingStrategy=${this.floatingStrategy}
          maxHeight="auto"
          offset="4">
          <div
            slot="trigger"
            part="trigger"
            class="${clsx('select', `select--${this.size}`, {
              'select--disabled': this.disabled,
              'select--readonly': this.readonly,
              'select--opened': this.opened,
              'select--loading': this.loading,
              'select--success': this.status === 'success',
              'select--error': this.status === 'error',
            })}">
            <input
              part="input"
              role="combobox"
              class="${clsx('input', `input--${this.size}`, {
                'input--disabled': this.disabled,
              })}"
              id="input"
              value=${this.getFormattedValue()}
              aria-expanded=${this.opened}
              aria-haspopup="dialog"
              aria-labelledby=${ifDefined(this.label ? 'label' : nothing)}
              aria-describedby="${ifDefined(
                this.description ? 'description' : nothing,
              )} ${ifDefined(this.feedback ? 'feedback' : nothing)}"
              aria-disabled=${this.disabled}
              aria-controls="calendar"
              aria-autocomplete="none"
              ?required=${this.required}
              ?readonly=${this.readonly}
              ?autofocus=${this.autofocus}
              ?disabled=${this.disabled}
              autocomplete=${this.autocomplete as any}
              placeholder=${ifDefined(this.placeholder ?? '')}
              @blur=${this.handleBlur}
              @focus=${this.handleFocus}
              @input=${this.handleInput}
              @keydown=${this.handleKeyDown}
              @invalid=${this.handleInvalid}
              @click=${this.handleClick} />
            <div
              class="${clsx('select-action', `select-action--${this.size}`, {
                'select-action--disabled': this.disabled,
                'select-action--readonly': this.readonly,
                'select-action--opened': this.opened,
              })}">
              ${this.clearButton === 'true'
                ? html`<div class="${clsx('clear-icon')}">
                    <dap-ds-icon-button
                      icon="close-line"
                      part="clear-button"
                      aria-label=${ifDefined(
                        this.clearButtonAriaLabel ?? translate('clear'),
                      )}
                      ?disabled=${this.disabled}
                      @click=${this.handleClearClick}></dap-ds-icon-button>
                  </div>`
                : nothing}
              <div
                class="${clsx('indicator-icon', {
                  'indicator-icon--loading': this.loading,
                })}">
                ${this.loading
                  ? html`<dap-ds-icon-loading-spinner></dap-ds-icon-loading-spinner>`
                  : html`<dap-ds-icon-calendar-line
                      aria-hidden="true"
                      part="calendar-button"
                      class=${clsx('calendar-icon')}
                      @click=${this.handleClick}></dap-ds-icon-calendar-line>`}
              </div>
            </div>
          </div>
          <dap-ds-calendar
            id="calendar"
            part="calendar"
            locale=${this.locale}
            @dds-close=${this.hideCalendar}
            @dds-change=${(event: CustomEvent) => {
              event.preventDefault()
              event.stopPropagation()
              this.setValue(event.detail.value)
            }}>
          </dap-ds-calendar>
        </dap-ds-popup>
        ${this.feedback &&
        html`<dap-ds-feedback
          part="feedback"
          feedbackType=${ifDefined(this.feedbackType)}
          feedbackSize="${ifDefined(this.size as 'lg' | 'sm' | 'xs')}"
          id="feedback">
          ${this.feedback}
        </dap-ds-feedback>`}
      </div>`
  }
}
